import React, { useState } from 'react';
import { Alert, AlertTitle, Button, Box, LinearProgress, Typography } from '@mui/material';
import { useSubscription } from '../../contexts/SubscriptionContext';

/**
 * Renders a sticky banner at the top of the app when the subscription is
 * expiring soon, in trial, or expired. Dismissable per session.
 */
export default function SubscriptionBanner() {
  const { warningBanner, subscription, isSuperAdmin } = useSubscription();
  const [dismissed, setDismissed] = useState(false);

  if (!warningBanner || dismissed || isSuperAdmin) return null;

  const { type, message } = warningBanner;

  const severity = type === 'expired' || type === 'trial_expired'
    ? 'error'
    : type === 'trial_expiring' ? 'warning'
    : 'info';

  const showProgress = (type === 'trial_expiring' || type === 'expiring') && subscription.daysLeft != null;
  const totalDays = type === 'trial_expiring' ? 14 : 365;
  const progress  = showProgress ? Math.max(0, Math.round((subscription.daysLeft / totalDays) * 100)) : null;

  return (
    <Alert
      severity={severity}
      onClose={() => setDismissed(true)}
      sx={{
        borderRadius: 0, position: 'sticky', top: 0, zIndex: 1200,
        '& .MuiAlert-message': { width: '100%' },
      }}
      action={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {(type === 'trial_expiring' || type === 'trial_expired') && (
            <Button
              size="small" variant="outlined" color="inherit"
              href="/admin" onClick={e => { e.preventDefault(); window.location.href = '/admin'; }}
              sx={{ whiteSpace: 'nowrap', fontWeight: 700, fontSize: '0.75rem' }}
            >
              Upgrade now
            </Button>
          )}
          {(type === 'expiring' || type === 'expired') && (
            <Button
              size="small" variant="outlined" color="inherit"
              href="/admin" onClick={e => { e.preventDefault(); window.location.href = '/admin'; }}
              sx={{ whiteSpace: 'nowrap', fontWeight: 700, fontSize: '0.75rem' }}
            >
              Contact admin
            </Button>
          )}
        </Box>
      }
    >
      <AlertTitle sx={{ fontWeight: 700, mb: 0.25 }}>
        {type === 'trial_expiring' || type === 'trial_expired' ? 'Free Trial' : 'Subscription'}
        {' '}
        {type.includes('expir') ? (type.includes('ed') ? 'Expired' : 'Expiring Soon') : ''}
      </AlertTitle>
      <Typography variant="body2">{message}</Typography>
      {showProgress && (
        <Box sx={{ mt: 0.75 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            color={severity === 'error' ? 'error' : severity === 'warning' ? 'warning' : 'info'}
            sx={{ height: 4, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.08)' }}
          />
          <Typography variant="caption" sx={{ opacity: 0.75 }}>
            {subscription.daysLeft} day{subscription.daysLeft !== 1 ? 's' : ''} remaining
          </Typography>
        </Box>
      )}
    </Alert>
  );
}
